import {ReactNode} from "react";
import {OtsBadgeTone} from "./OtsBadge";

export type OtsTimelineTone = "primary" | OtsBadgeTone;

export type OtsTimelineItem = {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  current?: boolean;
  tone?: OtsTimelineTone;
};

export type OtsTimelineProps = {
  items: OtsTimelineItem[];
  ariaLabel: string;
  density?: "compact" | "default";
  emptyState?: ReactNode;
  className?: string;
};

const toneClasses: Record<OtsTimelineTone, string> = {
  primary: "bg-[var(--ots-primary)]",
  neutral: "bg-[var(--ots-outline)]",
  info: "bg-[var(--ots-primary)]",
  success: "bg-[var(--ots-success)]",
  warning: "bg-[var(--ots-warning)]",
  danger: "bg-[var(--ots-error)]",
  blue: "bg-[var(--ots-blue)]",
  yellow: "bg-[var(--ots-yellow)]",
  violet: "bg-[var(--ots-violet)]",
  indigo: "bg-[var(--ots-indigo)]",
};

/** Reusable chronological history for entity status, workflow, and processing events. */
export const OtsTimeline = ({
  items,
  ariaLabel,
  density = "default",
  emptyState = "No history is available.",
  className,
}: OtsTimelineProps) => {
  if (!items.length) return <p className={["text-sm text-[var(--ots-text-muted)]", className].filter(Boolean).join(" ")}>{emptyState}</p>;

  return <ol aria-label={ariaLabel} className={[density === "compact" ? "space-y-3" : "space-y-4", className].filter(Boolean).join(" ")}>
    {items.map((item, index) => <li key={item.id} className="relative flex gap-3">
      {index < items.length - 1 && <span aria-hidden="true" className="absolute left-[4px] top-4 h-[calc(100%+0.25rem)] w-px bg-[var(--ots-outline-variant)]"/>}
      <span aria-hidden="true" className={[
        "relative z-10 mt-1.5 size-2.5 shrink-0 rounded-full ring-4 ring-[var(--ots-surface)]",
        toneClasses[item.tone ?? (item.current ? "primary" : "neutral")],
      ].join(" ")}/>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[var(--ots-secondary)]">{item.label}</p>
        {item.description != null && <div className="mt-0.5 text-xs text-[var(--ots-text-muted)]">{item.description}</div>}
      </div>
    </li>)}
  </ol>;
};
